import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import slugify from 'slugify';

import { useDateGroups } from '../../hooks/useDates';
// import { useItemsPaginated } from '../../hooks/useItems';
import useItemsStore from '../../hooks/useItemsStore';

// import * as AppPropTypes from '../../lib/PropTypes';
import { useApi } from '../../contexts/ApiContext';
import { useItemRefs } from '../../contexts/ItemRefContext';
import { useModal } from '../../contexts/ModalsContext';
import { useUrlFromHandle } from '../../contexts/RoutesContext';
import SmallTicket from '../icons/SmallTicket';
import NoEvents from '../partials/NoEvents';
import NoFilms from '../partials/NoFilms';
import CalendarList from './CalendarList';
import CardsList from './CardsList';
import LoadingList from './LoadingList';

import styles from '../../../styles/lists/section-list.module.scss';

const propTypes = {
    items: PropTypes.object, // eslint-disable-line
    title: PropTypes.string,
    description: PropTypes.string,
    listType: PropTypes.string,
    url: PropTypes.string,
    initialHash: PropTypes.string,
    isDark: PropTypes.bool,
    withHeader: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    items: null,
    title: null,
    description: null,
    listType: 'bundles',
    url: null,
    initialHash: null,
    isDark: false,
    withHeader: true,
    className: null,
};

const SectionList = ({
    items: initialItems,
    title,
    description,
    listType,
    url,
    initialHash,
    isDark,
    withHeader,
    className,
}) => {
    const api = useApi();
    const { setModal } = useModal();
    const route = useUrlFromHandle();
    const intl = useIntl();
    const [query, setQuery] = useState({ url });
    useEffect(() => {
        setQuery({ url, page: 1 });
    }, [url]);
    const { page: queryPage = 1, count: queryCount = 10 } = query || {};

    const { scrollToRef, currentItem, selectRef } = useItemRefs();
    const isEvents = listType === 'events';
    const isBundles = listType === 'bundles';

    const getPage = useCallback(
        (page, count) =>
            api.sections
                .findByUrl(url, {
                    count,
                    page,
                })
                .then((response) => {
                    const { pages: responseItems } = response || {};
                    return responseItems;
                }),
        [api, url, listType],
    );

    console.log('initialItems', initialItems);

    // const initialPages = useMemo(
    //     () => (initialItems !== null ? [initialItems] : null),
    //     [url, listType],
    // );
    // console.log('listType', listType);

    const {
        allItems: items,
        loading,
        allLoaded,
        loadNextPage,
    } = useItemsStore(listType, {
        getPage,
        count: 10,
        // pages: initialPages,
        query,
        onQueryChange: setQuery,
    });

    const groups = useDateGroups(isEvents ? items : null, 'YYYY-MM-DD', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    });

    const finalItems = isEvents ? groups : items;

    const headingProps = useMemo(
        () => ({
            title,
            description,
            // url, // TODO: think about this for heading
            type: 'section',
        }),
        [title, url, description],
    );

    // Not a simple one
    const openFilmModal = useCallback(
        (item, props) => {
            if (isBundles) {
                const { id: itemId = null } = item || {};
                const fullBundle =
                    (finalItems || []).reduce((acc, bundle) => {
                        const { films } = bundle || {};
                        const exists = films.find(({ id = null }) => id === itemId) || null;
                        if (exists !== null) {
                            return bundle;
                        }
                        return acc;
                    }) || null;
                if (fullBundle !== null) {
                    const { title: bundleTitle = null, films = [] } = fullBundle || {};
                    setModal({
                        type: 'films',
                        item,
                        fullScreen: true,
                        loadNextPage,
                        allLoaded,
                        items: films,
                        heading: { ...headingProps, title: bundleTitle },
                        offset: 210,
                        withAutoScroll: true,
                        withoutTitleBar: true,
                        ...props,
                    });
                }
            } else {
                const modalItems = isEvents
                    ? (items || [])
                          .map(({ film = null }) => film)
                          .filter((film) => film !== null)
                          .reduce((films, film) => {
                              const { id: filmId = null } = film || {};
                              const exists = films.find(({ id = null }) => id === filmId) || null;
                              if (exists === null) {
                                  films.push(film);
                              }
                              return films;
                          }, [])
                    : finalItems;
                setModal({
                    type: 'films',
                    item,
                    fullScreen: true,
                    loadNextPage,
                    allLoaded,
                    items: modalItems,
                    heading: headingProps,
                    withAutoScroll: true,
                    withoutTitleBar: true,
                    ...props,
                });
            }
        },
        [setModal, items, finalItems, loadNextPage, headingProps, allLoaded, isEvents, isBundles],
    );

    // Trigger hash from item change
    useEffect(() => {
        if (currentItem) {
            const { id: currentId = null } = currentItem || {};
            if (currentId === null) {
                return;
            }
            const item =
                (finalItems || []).find((it) => {
                    const { id } = it || {};
                    return id === currentId;
                }) || null;
            if (item !== null) {
                const { title: itemTitle = null } = item;
                const hash = isEvents ? currentId : itemTitle || null;
                if (hash !== null) {
                    // window.location.replace(`#${slugify(hash, { strict: true })}`);
                }
            }
        }
    }, [currentItem, isEvents]);

    // Initial scroll or activate dot
    useEffect(() => {
        const item =
            (finalItems || []).find((it) => {
                const { title: itemTitle = null, id: itemId = null } = it || {};
                if (isEvents) {
                    return initialHash === itemId;
                }
                return initialHash === slugify(itemTitle, { strict: true });
            }) || null;
        if (item !== null) {
            // Do not question this
            setTimeout(() => {
                scrollToRef({ ...item, type: 'cardsList' }, 120);
            }, 0);
        } else {
            const [first] = finalItems || [];
            const { id = null } = first || {};
            selectRef({ id, type: 'cardsList' });
        }
    }, [initialHash, isEvents]);

    const emptyElement =
        listType === 'events' ? <NoEvents isDark={isDark} /> : <NoFilms isDark={isDark} />;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            <LoadingList
                loading={loading}
                onClickMore={loadNextPage}
                ended={allLoaded}
                withMore
                withAutoLoad
            >
                {listType === 'films' ? (
                    <CardsList
                        className={styles.group}
                        items={finalItems}
                        isGrid
                        listClassName={classNames([
                            styles.groupCards,
                            {
                                [styles.noHeader]: !withHeader,
                            },
                        ])}
                        withMetadata
                        withRef
                        isDark={isDark}
                        cardProps={{ openFilmModal, isModal: true }}
                    />
                ) : null}
                {listType === 'bundles' && finalItems !== null
                    ? finalItems.map(
                          (
                              {
                                  films = [],
                                  id = null,
                                  title: bundleTitle = null,
                                  description: bundleDescription = null,
                                  screenings = null,
                              },
                              index,
                          ) => {
                              let link = null;
                              const hasBundleDescription =
                                  finalItems.length > 1 && bundleDescription !== null;

                              if (screenings !== null && screenings.length > 0) {
                                  const defaultUrl = `${route('calendar')}?bundles=${id}`;
                                  if (screenings.length === 1) {
                                      const [first = null] = screenings || [];
                                      const { ticketsUrl } = first || {};
                                      link = {
                                          href: ticketsUrl !== null ? ticketsUrl : defaultUrl, // firstUrl,
                                          label: intl.formatMessage({
                                              defaultMessage: 'Buy tickets',
                                              description: 'Button label',
                                          }),
                                          icon: <SmallTicket />,
                                          external: ticketsUrl !== null,
                                      };
                                  } else {
                                      link = {
                                          href: defaultUrl,
                                          label: intl.formatMessage({
                                              defaultMessage: 'Choose tickets',
                                              description: 'Button label',
                                          }),
                                          icon: <SmallTicket />,
                                      };
                                  }
                              }
                              return (
                                  <CardsList
                                      key={`${bundleTitle}-list-${index + 1}`}
                                      className={styles.group}
                                      id={id}
                                      title={bundleTitle}
                                      items={films}
                                      isGrid
                                      withStickyHeader
                                      headerClassName={classNames([
                                          styles.stickyHeader,
                                          { [styles.withMargin]: hasBundleDescription },
                                      ])}
                                      listClassName={styles.groupCards}
                                      withMetadata
                                      withRef
                                      isDark={isDark}
                                      link={link}
                                      cardProps={{ openFilmModal, isModal: true }}
                                  >
                                      {hasBundleDescription ? (
                                          <p className={styles.bundleDescription}>
                                              {bundleDescription}
                                          </p>
                                      ) : null}
                                  </CardsList>
                              );
                          },
                      )
                    : null}
                {listType === 'events' ? (
                    <CalendarList
                        groups={finalItems}
                        withStickyHeader
                        cardProps={{ openFilmModal, isModal: true }}
                        isDark={isDark}
                    />
                ) : null}
                {(finalItems || []).length === 0 && !loading ? (
                    <div className={styles.emptyElement}>{emptyElement}</div>
                ) : null}
            </LoadingList>
        </div>
    );
};

SectionList.defaultProps = defaultProps;
SectionList.propTypes = propTypes;

export default SectionList;
